import {
	ArrowLeft,
	Box,
	Clock,
	MapPin,
	MessageCircleMoreIcon,
	Truck
} from "lucide-react";

import { useOrder } from "@/api/order-query";
import { usePaymentTransaction } from "@/api/verify-payments";
import Countdown from "@/components/dashboard/countdown";
import { AcceptOrder } from "@/components/order/accept-order";
import { ContactInfo } from "@/components/order/contact-info";
import { OrderItem } from "@/components/order/order-item";
import { ConfirmPayment } from "@/components/order/payment-dialog";
import { RejectOrder } from "@/components/order/reject-order";
import { StatusTag } from "@/components/order/status-tag";
import { TimelineItem } from "@/components/order/timeline-item";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogTrigger
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import {
	cn,
	formatDate,
	formatRelativeDate,
	genOrderProgressVal
} from "@/lib/utils";
import { PaymentProvider } from "@/types/banks";
import {
	DispatchStatusDisplayText,
	DispatchStatusEnum,
	OrderStatusEnum
} from "@/types/order";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";

export const Route = createFileRoute("/buyer/orders/$orderid/")({
	component: OrderDetail
});

export function OrderDetail() {
	const router = useRouter();
	const tranx = usePaymentTransaction();
	const { orderid } = Route.useParams();
	const { data: order, isPending, isError, refetch } = useOrder(orderid);
	if (isPending) {
		return (
			<div className="mx-auto max-w-7xl p-4 md:p-6">
				<Button variant="secondary" className="gap-2 bg-white mb-4">
					<ArrowLeft className="h-4 w-4" />
					<span>Back</span>
				</Button>
				<Card className="mb-6 p-6 shadow-none space-y-4 min-h-[80vh] min-w-[60vw] grid place-content-center">
					<div className="flex flex-col items-center justify-center gap-4">
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
						<p className="text-muted-foreground">Loading order details...</p>
					</div>
				</Card>
			</div>
		);
	}
	if (isError) {
		return (
			<div className="mx-auto max-w-7xl p-4 md:p-6">
				<Button variant="secondary" className="gap-2 bg-white mb-4">
					<ArrowLeft className="h-4 w-4" />
					<span>Back</span>
				</Button>
				<Card className="mb-6 p-6 shadow-none space-y-4 min-h-[80vh] min-w-[60vw] grid place-content-center">
					<div className="text-center space-y-4">
						<div className="text-destructive text-5xl mb-4">⚠️</div>
						<h2 className="text-xl font-semibold text-destructive">
							Error fetching data
						</h2>
						<p className="text-muted-foreground">Please try again later</p>
					</div>
				</Card>
			</div>
		);
	}
	const total = order.contract.contractItem.reduce(
		(sum, item) => sum + item.totalCost,
		0
	);
	const handlePayment = async (newRef = false) => {
		await tranx.tranxInit.mutateAsync({
			newRef,
			contractId: order.contractId,
			orderAmount: total,
			courierPickupOfficeId: String(order.pickupLocation.id),
			paymentOption: PaymentProvider.PAYSTACK
		});
		await tranx.processPayment({
			contractId: order.contractId,
			orderAmount:
        total
        + Number(tranx.tranxInit.data?.order?.transactionFee)
        + Number(tranx.tranxInit.data?.order?.estimatedCourierFee),
			courierPickupOfficeId: String(order.pickupLocation.id),
			paymentOption: PaymentProvider.PAYSTACK
		});
		await refetch();
	};

	const calculateCountDownSeconds = () => {
		const paid = new Date(order.contract.updatedAt);

		const deadline = new Date(paid.getTime() + 24 * 60 * 60 * 1000);

		const diff = deadline.getTime() - new Date().getTime();

		return Math.floor(diff / 1000);
	};

	return (
		<div className="mx-auto w-full max-w-7xl p-4 md:p-6">
			<Button
				variant="secondary"
				className="gap-2 bg-white mb-4"
				onClick={() => router.history.back()}
			>
				<ArrowLeft className="h-4 w-4" />
				<span>Back</span>
			</Button>
			<div className="mb-6 flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-medium">Order detail</h1>
				</div>
				<Button
					variant="ghost"
					className="text-primary hover:text-primary/40 items-center gap-2"
					asChild
				>
					<Link to="/buyer/orders/$orderid/chat" params={{ orderid }}>
						Chat with vendor
						<MessageCircleMoreIcon className="size-4" />
					</Link>
				</Button>
			</div>

			<Card className="mb-6 p-6 shadow-none space-y-4">
				<div className="flex flex-wrap justify-between">
					<div className="mt-1">
						<span className="text-gray-500">Contract ID: </span>
						<span className="font-medium">{order?.contract.contractCode}</span>
					</div>

					{order.status === OrderStatusEnum.PENDING && (
						<Dialog>
							<DialogTrigger asChild>
								<Button variant="accent" onClick={() => handlePayment(true)}>
									Complete Payment
								</Button>
							</DialogTrigger>
							<DialogContent className="sm:max-w-[425px]">
								<ConfirmPayment
									state=""
									label="Confirm Details"
									total={total}
									isPending={false}
									courierBranchName={order.pickupCourier.name}
									courierBranchAddress={order.pickupLocation.address}
									handlePayment={handlePayment}
									fee={{
										intasida: 0,
										courier: 0
									}}
								/>
							</DialogContent>
						</Dialog>
					)}

					<div
						className={cn("hidden items-center gap-3", {
							flex:
                order.dispatchStatus === DispatchStatusEnum.Arrived
                && order.status === OrderStatusEnum.PAID
						})}
					>
						<div className="flex items-center gap-3">
							<RejectOrder orderId={orderid} onSuccess={refetch} />
							<AcceptOrder orderId={orderid} onSuccess={refetch} />
						</div>
					</div>

					{order.status === OrderStatusEnum.REJECTED && (
						<span
							className={cn(
								"bg-red-200 text-red-800 px-1.5 py-1 capitalize rounded-full text-xs flex items-center justify-center"
							)}
						>
							{order.status.toLowerCase()}
						</span>
					)}
				</div>
				<div className="grid grid-cols-6 gap-2">
					<div className="bg-gray-50 col-span-4 max-lg:col-span-6 h-40 p-3 rounded-lg">
						<div className="mb-6 flex items-start justify-between gap-4">
							<Box className="h-6 w-6 text-primary" />

							<StatusTag status={order.dispatchStatus} />
						</div>
						{order.dispatchStatus === DispatchStatusEnum.Pending
							? (
									<>
										<div className="text-lg font-medium text-red-500">This order has not been dispatched yet</div>
										<span className="text-sm text-muted-foreground font-thin">Once the 24hr countdown is over, you can choose to cancel this order and a refund will be made.</span>
									</>
								)
							: (
									<>
										<div className="text-base font-medium">
											{DispatchStatusDisplayText[order.dispatchStatus]}
										</div>
										<div className="mb-4 flex justify-between items-center gap-4">
											<div className="flex items-center gap-2 text-xs bg-neutral-200 rounded-lg p-1">
												<Truck className="size-4" />
												{order.dropoffLocation?.address ?? "Unavailable"}
											</div>
											<div className="h-px flex-1 border-t border-dashed border-gray-300" />
											<div className="flex items-center gap-2 text-xs rounded-lg p-1 text-right bg-neutral-200">
												<MapPin className="size-4" />
												{order.pickupLocation.address}
											</div>
										</div>
										<Progress value={genOrderProgressVal(order.dispatchStatus)} />
									</>
								)}
					</div>

					{order.dispatchStatus !== DispatchStatusEnum.Pending
						? (
								<>
									<div className="flex flex-col bg-gray-100 rounded-lg p-2 items-start justify-end gap-3 max-lg:col-span-3">
										<Clock className="size-10 p-2 rounded-full text-primary bg-hover-secondary" />
										<div>
											<div className="text-sm text-gray-500">Estimated arrival</div>
											<div className="font-medium">
												{formatDate(order.contract.estimatedDeliveryDate)}
											</div>
										</div>
									</div>
									<div className="flex flex-col bg-gray-100 rounded-lg p-2 items-start justify-end gap-3 max-lg:col-span-3">
										<Truck className="size-10 p-2 rounded-full text-primary bg-hover-secondary" />
										<div>
											<div className="text-sm text-gray-500">Delivered</div>
											<div className="font-medium">
												{formatRelativeDate(order.contract.estimatedDeliveryDate)}
											</div>
										</div>
									</div>
								</>
							)
						: (
								<div className="flex flex-col bg-gray-100 rounded-lg p-2 items-start justify-end gap-3 col-span-2 max-lg:col-span-6">
									<Clock className="size-10 p-2 rounded-full text-primary bg-hover-secondary" />
									<div className="flex items-center gap-2">
										<div className="text-sm text-gray-500">Ship Out Deadline:</div>
										<span className="bg-green-500 text-white px-3 py-1 rounded-md text-lg flex items-center justify-center">
											<Countdown seconds={calculateCountDownSeconds()} />
										</span>
									</div>
								</div>
							)}

					<Card className="p-6 shadow-none col-span-3 max-lg:col-span-6">
						<h2 className="mb-4 text-lg font-medium">Timeline</h2>
						<div className="divide-y">
							{!order.trackings.length && (
								<div className="my-12 text-muted-foreground text-center">
									Timeline Unavailable
								</div>
							)}
							{order.trackings
								.sort(
									(a, b) =>
										new Date(b.timestamp).getTime()
											- new Date(a.timestamp).getTime()
								)
								.map((tracking, i) => (
									<TimelineItem
										key={tracking.id}
										date={formatDate(tracking.timestamp, {
											day: "numeric",
											month: "long"
										})}
										time={new Date(tracking.timestamp).toLocaleTimeString(
											"en-US",
											{
												hour: "numeric",
												minute: "numeric",
												hour12: true
											}
										)}
										isActive={!i}
										description={tracking.dispatchStatus}
									/>
								))}
						</div>
					</Card>

					<Card className="p-6 shadow-none col-span-3  max-lg:col-span-6">
						<h2 className="mb-4 text-lg font-medium">Shipment</h2>
						<div className="mb-4 flex items-center gap-3">
							<div className="h-10 w-10 rounded-full bg-gray-100 p-2">
								<Box className="h-6 w-6" />
							</div>
							<div>
								<div className="font-medium">Intasida Technologies</div>
								<div className="text-sm text-gray-500">
									{order.dropoffCourier?.name}
								</div>
							</div>
						</div>
						<div className="divide-y">
							<ContactInfo
								label="Courier agent (Drop off)"
								name={order.dropoffCourier?.name}
								number={order.dropoffCourier?.phoneNumber}
							/>
							<ContactInfo
								label="Courier agent (Pick up)"
								name={order.pickupCourier.name}
								number={order.pickupCourier.phoneNumber}
							/>
						</div>
					</Card>
				</div>
				<div>
					<h2 className="mb-4 text-lg font-medium">Items</h2>
					<div className="grid grid-cols-2 gap-2 max-lg:grid-cols-1">
						{order.contract.contractItem.map(item => (
							<OrderItem
								key={item.id}
								quantity={Number(item.productQuantity)}
								image={
									item.productId?.photos.length > 0
										? item.productId.photos[0]
										: "/placeholder.svg"
								}
								name={item.productId?.name}
								price={item.totalCost}
							/>
						))}
					</div>
				</div>
			</Card>
		</div>
	);
}
